import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import LoadingSpinner from "../../LoadingSpinner";
import SearchDropdown from "../../input/SearchDropdown";
import { getBehalfOf, getUsernameById } from "../../../api/systemUserApi";
import { AUTH_TYPES, FORMS, MODULES } from "../../../const";
import Input from "../../input/Input";
import Dropdown from "../../input/Dropdown";
import Button from "../../input/Button";
import Grid from "../../Grid";
import {
  ActiveCellStyle,
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../renderer/StyleRenderer";
import ButtonRenderer from "../../renderer/ButtonRenderer";
import { isNumber } from "../../../helper";
import {
  getBulkDropdownData,
  getDropdownData,
  getTextByValueAndKey,
} from "../../../api/keyvalue";
import { alertValidation } from "../../validations/AlertCreation";
import { shiftUserValidation } from "../../validations/ShiftCreation";
import { shiftUserFindByUserId } from "../../../api/shiftApi";
import { toast } from "react-toastify";
import InfoList from "../../InfoList";
import { formatFromJsonDate } from "../../../dateConfig";
import { User } from "react-feather";
import useUserCompany from "../../../hooks/useUserCompany";
import DeleteButtonRenderer from "../../renderer/DeleteButtonRenderer";

const FORM = FORMS.CALL_CENTER_SHIFT.SHIFT_USER.form;
const ShiftUsersForm = forwardRef(
  ({ isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();

    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null,
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const [shiftUserData, setsShiftUserData] = useState({
      shuCode: "",
      shuReferance: "",
      shuCompany: company,
      shuUser: null,
      shuUserName: "",
      shuDescription: "",
      shuStatus: 2,
      shuActive: 1,
    });
    // errors
    const [currentErrors, setCurrentErrors] = useState({});

    const [currentShiftUsers, setCurrentShiftUsers] = useState({});
    const [shiftUsers, setShiftUsers] = useState([]);
    const [shiftUsersCount, setShiftUsersCount] = useState(2);
    const [deletedList, setDeletedList] = useState([]);
    const [mFields, setMFields] = useState({});
    const [auditInfo, setAuditInfo] = useState({});

    // bool
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [searchFieldReset, setSearchFieldReset] = useState(false);

    //dropdown
    const [activeOptions, setActiveOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);

    useEffect(() => {
      (async () => {
        //new
        const { active, status } = await getBulkDropdownData([
          { key: "active" },
          { key: "status" },
        ]);

        setActiveOptions(active);
        setStatusOptions(status);
      })();
    }, []);

    const onAddClick = async () => {
      setIsAddLoading(true);

      let shiftUserErrors = shiftUserValidation({
        ...shiftUserData,
      });

      if (Object.keys(shiftUserErrors).length === 0) {
        const response = await shiftUserFindByUserId(shiftUserData.shuUser);

        if (
          Object.keys(currentShiftUsers).length === 0 &&
          shiftUsers.some((user) => user?.shuUser === shiftUserData.shuUser)
        ) {
          toast.error(
            "There was an error adding shift user already added to grid!",
            { autoClose: 5000, containerId: "Main" },
          );
        } else if (!shiftUserData.id && response !== "") {
          toast.error("There was an error adding shift user already exists!", {
            autoClose: 5000,
            containerId: "Main",
          });
        } else {
          const statusValue = await getTextByValueAndKey(
            "status",
            shiftUserData.shuStatus,
          );
          const activeValue = await getTextByValueAndKey(
            "active",
            shiftUserData.shuActive,
          );
          const username = await getUsernameById(shiftUserData.shuUser);

          if (!shiftUserData.shuId || shiftUserData.shuId === null) {
            if (!shiftUserData.id || shiftUserData.id === null) {
              setShiftUsers((prevState) => [
                ...prevState,
                {
                  ...shiftUserData,
                  id: shiftUsersCount,
                  user: username,
                  status: statusValue,
                  active: activeValue,
                },
              ]);
              setShiftUsersCount((prevState) => ++prevState);
            } else {
              setShiftUsers((prevState) =>
                prevState.filter((item) => item.id !== shiftUserData.id),
              );
              setShiftUsers((prevState) => [
                ...prevState,
                {
                  ...shiftUserData,
                  user: username,
                  status: statusValue,
                  active: activeValue,
                },
              ]);
            }
          } else {
            setShiftUsers((prevState) =>
              prevState.filter((item) => item.shuId !== shiftUserData.shuId),
            );
            setShiftUsers((prevState) => [
              ...prevState,
              {
                ...shiftUserData,
                modifed: true,
                user: username,
                status: statusValue,
                active: activeValue,
              },
            ]);
          }
          setButtonClicked(true);
        }
      }

      setCurrentErrors(shiftUserErrors);
      setIsAddLoading(false);
    };

    useEffect(() => {
      if (buttonClicked) {
        setsShiftUserData({
          shuCode: "",
          shuReferance: "",
          shuCompany: company,
          shuUser: null,
          shuUserName: "",
          shuDescription: "",
          shuStatus: 2,
          shuActive: 1,
        });
        setButtonClicked(false);
        setSearchFieldReset(true);
        setCurrentShiftUsers({});
      }
    }, [buttonClicked]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            deleted: deletedList,
          };

          shiftUsers.forEach((shu) => {
            if (shu.shuId && shu.shuId !== null) {
              if (shu.modifed) {
                updateState.modified.push(shu);
              }
            } else {
              updateState.created.push(shu);
            }
          });

          return updateState;
        } else {
          return shiftUsers;
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setShiftUsers([]);
      },
    }));

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentClusters = [];
          let id = 2;
          for (let currentValue of currentValues) {
            const statusValue = await getTextByValueAndKey(
              "status",
              currentValue.shuStatus,
            );
            const activeValue = await getTextByValueAndKey(
              "active",
              currentValue.shuActive,
            );
            const username = await getUsernameById(currentValue.shuUser);
            currentClusters.push({
              ...currentValue,
              status: statusValue,
              active: activeValue,
              user: username,
              shuCompany: company,
              id,
            });
            ++id;
          }
          setShiftUsersCount(id);
          setShiftUsers(currentClusters);
          setAuditInfo({
            createdDate: currentValues[0].createdDate,
            createdBy: currentValues[0].createdBy,
            lastModifiedBy: currentValues[0].lastModifiedBy,
            lastModifiedDate: currentValues[0].lastModifiedDate,
          });
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (Object.keys(currentShiftUsers).length > 0) {
        setsShiftUserData({
          ...currentShiftUsers,
        });
      }
    }, [editMode, currentShiftUsers]);

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Users</h4>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  disabled={editMode}
                  required={true}
                  editMode={editMode}
                  multiGrid={true}
                  multiGridCurrentValue={shiftUserData.user}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  api={getBehalfOf}
                  apiDependency={{
                    customer: null,
                    shipper: null,
                  }}
                  onResultSelect={(data) =>
                    setsShiftUserData((prevState) => ({
                      ...prevState,
                      shuUser: data,
                    }))
                  }
                  onClear={() => {
                    setsShiftUserData((prevState) => ({
                      ...prevState,
                      shuUser: null,
                    }));
                  }}
                  error={currentErrors.shuUser}
                  resetError={() => {
                    setCurrentErrors((prevState) => {
                      const { shuUser: _, ...rest } = prevState;
                      return rest;
                    });
                    setCurrentErrors((prevState) => {
                      const { shuUser: _, ...rest } = prevState;
                      return rest;
                    });
                  }}
                  label="User"
                  field="req_behalf_of"
                />
                <Input
                  label="Description"
                  error={currentErrors.shuDescription}
                  module={MODULES.Master}
                  form={FORM}
                  field="shi_description"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { shuDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Description"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                  value={shiftUserData.shuDescription}
                  onChange={(e) => {
                    setsShiftUserData((prevState) => ({
                      ...prevState,
                      shuDescription: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Status"
                  error={currentErrors.shuStatus}
                  module={MODULES.Master}
                  form={FORM}
                  field="blk_status"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { shuStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Status"
                  options={statusOptions}
                  value={shiftUserData.shuStatus}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setsShiftUserData((prevState) => ({
                      ...prevState,
                      shuStatus: data.value,
                    }));
                  }}
                />

                {/* <Dropdown
                  required={true}
                  label="Active"
                  error={currentErrors.shuActive}
                  module={MODULES.Master}
                  form={FORM}
                  field="blk_active"
                  // authenticated={true}
                  needDeletePermission={true}
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { shuActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={activeOptions}
                  value={shiftUserData.shuActive}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setsShiftUserData((prevState) => ({
                      ...prevState,
                      shuActive: data.value,
                    }));
                  }}
                /> */}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                template="reset"
                onClick={(e) => {
                  setButtonClicked(true);
                }}
              />
              <Button
                template="addOrSave"
                authenticated={true}
                editMode={editMode}
                module={MODULES.Master}
                form={`${FORM}`}
                create={Object.keys(currentShiftUsers).length === 0}
                disabled={
                  editMode && Object.keys(currentShiftUsers).length === 0
                }
                onClick={onAddClick}
                isLoading={isAddLoading}
              />
            </div>
            {/*<small style={{color: "#9f3a38"}}>{`${*/}
            {/*    currentErrors.mainCountError ? currentErrors.mainCountError : ""*/}
            {/*}`}</small>*/}
            <Grid
              heading="Users List"
              config={[
                { headerName: "User", field: "user" },
                {
                  headerName: "Description",
                  field: "shuDescription",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Status",
                  field: "status",
                  cellRenderer: StatusCellStyle,
                },
                {
                  headerName: "Active",
                  field: "active",
                  cellRenderer: ActiveCellStyle,
                },
                {
                  field: "Edit",
                  filter: true,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    consider: AUTH_TYPES.CREATE,
                    clickHandler: () => {
                      setCurrentShiftUsers(params.data);
                    },
                    label: "Edit",
                    authenticated: true,
                    form: `${FORM}`,
                    module: MODULES.Master,
                    editMode: editMode,
                  }),
                },
                {
                  field: "Delete",
                  filter: true,
                  cellRenderer: DeleteButtonRenderer,
                  cellRendererParams: (params) => ({
                    consider: AUTH_TYPES.DELETE,
                    clickHandler: () => {
                      if (params.data.shuId && params.data.shuId !== null) {
                        setDeletedList((prevState) => [
                          ...prevState,
                          params.data.shuId,
                        ]);
                      }
                      setShiftUsers((prevShift) =>
                        prevShift.filter(
                          (shift) => shift.id !== params.data.id,
                        ),
                      );
                    },
                    message: "Are you sure you want to delete this User?",
                    label: "Delete",
                    className: "ui red button",
                    authenticated: true,
                    form: `${FORM}`,
                    module: MODULES.Master,
                    editMode:
                      params.data.shuId !== null && isNumber(params.data.shuId),
                  }),
                },
              ]}
              content={shiftUsers}
            />
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: auditInfo.createdBy ? auditInfo.createdBy : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: auditInfo.createdDate
                      ? formatFromJsonDate(auditInfo.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: auditInfo.lastModifiedBy
                      ? auditInfo.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: auditInfo.lastModifiedDate
                      ? formatFromJsonDate(auditInfo.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);
export default ShiftUsersForm;
